/**
 * `pending-run-history-diff` v0 — wire contracts.
 *
 * Compares two existing test_runs rows (by id) and returns a
 * structured diff covering: top-level run stats (status, duration,
 * totals, browser, viewport, environment), per-step deltas (added,
 * removed, status-changed, duration-shifted), and a side-by-side
 * step list for the UI to render in two columns.
 *
 * No new tables — the diff is computed on-demand from the existing
 * `test_runs.step_results` JSONB blob.
 */
import { z } from 'zod';

export const RunDiffStepSideSchema = z.object({
  index: z.number().int().nonnegative(),
  name: z.string(),
  status: z.string(),
  durationMs: z.number().int().nonnegative().nullable(),
  errorMessage: z.string().nullable(),
});
export type RunDiffStepSide = z.infer<typeof RunDiffStepSideSchema>;

export const RunDiffStepRowSchema = z.object({
  // Stable identifier for the row across the two sides.
  key: z.string(),
  /**
   * Side-by-side payload. Either side can be null (added on B / removed on A).
   */
  a: RunDiffStepSideSchema.nullable(),
  b: RunDiffStepSideSchema.nullable(),
  // Convenience flags so the UI doesn't recompute.
  changed: z.boolean(),
  statusChanged: z.boolean(),
  durationShiftMs: z.number().int().nullable(),
});
export type RunDiffStepRow = z.infer<typeof RunDiffStepRowSchema>;

export const RunDiffSummarySchema = z.object({
  added: z.number().int().nonnegative(),
  removed: z.number().int().nonnegative(),
  statusChanged: z.number().int().nonnegative(),
  durationShifted: z.number().int().nonnegative(),
  unchanged: z.number().int().nonnegative(),
});
export type RunDiffSummary = z.infer<typeof RunDiffSummarySchema>;

export const RunDiffMetaSideSchema = z.object({
  id: z.string().uuid(),
  flowId: z.string().uuid(),
  flowName: z.string(),
  status: z.string(),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  durationMs: z.number().int().nonnegative(),
  totalSteps: z.number().int().nonnegative(),
  passedSteps: z.number().int().nonnegative(),
  failedSteps: z.number().int().nonnegative(),
  skippedSteps: z.number().int().nonnegative(),
  healedSteps: z.number().int().nonnegative(),
  browser: z.string(),
  viewport: z.string(),
  environment: z.string(),
});
export type RunDiffMetaSide = z.infer<typeof RunDiffMetaSideSchema>;

export const RunDiffResponseSchema = z.object({
  a: RunDiffMetaSideSchema,
  b: RunDiffMetaSideSchema,
  summary: RunDiffSummarySchema,
  steps: z.array(RunDiffStepRowSchema),
  generatedAt: z.string(),
});
export type RunDiffResponse = z.infer<typeof RunDiffResponseSchema>;
